import { LoadingIcon, OpenIcon } from '@blocksuite/affine-components/icons';
import type {
  EmbedVideoModel,
  EmbedVideoStyles,
} from '@blocksuite/affine-model';
import { ImageProxyService } from '@blocksuite/affine-shared/adapters';
import { ThemeProvider } from '@blocksuite/affine-shared/services';
import { BlockSelection } from '@blocksuite/std';
import { html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
// import videojs from 'video.js';

import { EmbedBlockComponent } from '../common/embed-block-element.js';
import { getEmbedCardIcons } from '../common/utils.js';
import { videoUrlRegex } from './embed-video-model.js';
import type { EmbedVideoBlockService } from './embed-video-service.js';
import { styles, VideoIcon } from './styles.js';
import { refreshEmbedVideoUrlData } from './utils.js';
import { createRef, ref } from 'lit/directives/ref.js';
// @ts-expect-error – no typings shipped for bundled video.js
import VideoJs from './video.js';

type VideoJsPlayer = {
  src(arg0: { src: string }): unknown;
  dispose(): unknown;
};

// @ts-expect-error
export class EmbedVideoBlockComponent extends EmbedBlockComponent<
  // @ts-ignore
  EmbedVideoModel,
  EmbedVideoBlockService
> {
  static override styles = styles;

  override _cardStyle: (typeof EmbedVideoStyles)[number] = 'video';

  open = () => {
    let link = this.model.props.url;
    if (!link.match(/^[a-zA-Z]+:\/\//)) {
      link = 'https://' + link;
    }
    window.open(link, '_blank');
  };

  refreshData = () => {
    refreshEmbedVideoUrlData(this, this.fetchAbortController.signal).catch(
      console.error
    );
  };

  private _handleDoubleClick(event: MouseEvent) {
    event.stopPropagation();
    this.open();
  }

  private _selectBlock() {
    const selectionManager = this.host.selection;
    const blockSelection = selectionManager.create(BlockSelection, {
      blockId: this.blockId,
    });
    selectionManager.setGroup('note', [blockSelection]);
  }

  protected _handleClick(event: MouseEvent) {
    event.stopPropagation();
    this._selectBlock();
  }

  override connectedCallback() {
    super.connectedCallback();
    this._cardStyle = this.model.props.style;

    if (!this.model.props.title) {
      this.store.withoutTransact(() => {
        const url = this.model.props.url;
        // Extract YouTube videoId only when the URL is a YouTube link
        const ytMatch = url.match(/\/([^\/?#]+)(?:\?[^#]*)?(?:#.*)?$/);
        const file = ytMatch ? decodeURIComponent(ytMatch[1]) : '';
        const title = file.replace(/\.[^./\\?&#]+$/, '') || file;
        if (title) {
          this.store.updateBlock(this.model, { title });
        }
      });
    }

    if (!this.model.props.description && !this.model.props.title) {
      this.store.withoutTransact(() => {
        this.refreshData();
      });
    }

    this.disposables.add(
      this.model.propsUpdated.subscribe(({ key }) => {
        this.requestUpdate();
        if (key === 'url') {
          this.refreshData();
        }
      })
    );

    matchMedia('print').addEventListener('change', () => {
      this._showImage = matchMedia('print').matches;
    });
  }

  override renderBlock() {
    const { image, description, title } = this.model.props;

    const loading = this.loading;
    const theme = this.std.get(ThemeProvider).theme;
    const imageProxyService = this.store.get(ImageProxyService);
    const { EmbedCardBannerIcon } = getEmbedCardIcons(theme);

    const descriptionText = loading ? null : description || title;
    const bannerImage =
      !loading && image
        ? html`<img src=${imageProxyService.buildUrl(image)} alt="banner" />`
        : EmbedCardBannerIcon;

    // Determine domain for display
    const linkUrl = this.model.props.url;
    let domain = '';
    try {
      const normalized = linkUrl.match(/^[a-zA-Z]+:\/\//)
        ? linkUrl
        : 'https://' + linkUrl;
      domain = new URL(normalized).host || domain;
    } catch {}

    const isDirectVideo = !!linkUrl && videoUrlRegex.test(linkUrl);

    return this.renderEmbed(
      () => html`
        <div
          class=${classMap({
            'affine-embed-videojs-block': true,
            loading,
            selected: this.selected$.value,
          })}
          style=${styleMap({
            transformOrigin: '0 0',
          })}
          @click=${this._handleClick}
          @dblclick=${this._handleDoubleClick}
        >
          <div class="affine-embed-videojs-video">
            ${isDirectVideo
              ? html`<video
                  ${ref(this._videoRef)}
                  class="affine-embed-videojs video-js vjs-theme-city"
                  controls
                  preload="metadata"
                  src=${linkUrl}
                  style="width: 100%; height: 100%; border-radius: 4px;"
                ></video>`
              : bannerImage}
          </div>
          <div class="affine-embed-videojs-content">
            <div class="affine-embed-videojs-content-url" @click=${this.open}>
              ${VideoIcon}
              ${loading
                ? nothing
                : html`<a href=${linkUrl} target="_blank"
                    >${descriptionText}</a
                  >`}
            </div>
          </div>
        </div>
      `
    );
  }

  private _videoRef = createRef<HTMLVideoElement>();

  override updated(): void {
    const linkUrl = this.model.props.url;
    const isDirectVideo = !!linkUrl && videoUrlRegex.test(linkUrl);
    const el = this._videoRef.value;
    if (isDirectVideo && el) {
      if (!this._videoPlayer) {
        const { player } = VideoJs(el, {
          controls: true,
          preload: 'metadata',
          maxHeight: 400,
          maxWidth: '100%',
        });
        player.src({ src: linkUrl });

        this._videoPlayer = player;
      }
    }
  }

  @state()
  private accessor _showImage = false;

  @property({ attribute: false })
  accessor loading = false;

  private accessor _videoPlayer: VideoJsPlayer | null = null;

  override disconnectedCallback(): void {
    if (this._videoPlayer) {
      this._videoPlayer.dispose();
      this._videoPlayer = null;
    }
    super.disconnectedCallback();
  }
}
